import React, { useState, useEffect, useCallback } from 'react';
import practiceService from '../../../services/practiceService';
import { isEvitalsMonitoringPractice } from '../../../utils/practiceMonitoringUtils';
import { matchesWordStart } from '../../../utils/searchMatch';
import SearchBar from '../../Common/SearchBar/SearchBar';
import ListScreenPaginationBar from '../../Common/ListScreenPaginationBar/ListScreenPaginationBar';
import './HeaderScreens.css';

const EmailNotificationsScreen = ({ onClose }) => {
  const [practices, setPractices] = useState([]);
  const [filteredPractices, setFilteredPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [updatingIds, setUpdatingIds] = useState(new Set());
  const [updateError, setUpdateError] = useState('');

  // Fetch practices on mount
  useEffect(() => {
    fetchPractices();
  }, []);

  // Prevent background body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Filter practices based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredPractices(practices);
    } else {
      const filtered = practices.filter((practice) =>
        matchesWordStart(practice.practice_name, searchTerm)
      );
      setFilteredPractices(filtered);
    }
    setCurrentPage(1); // Reset to first page when searching
  }, [searchTerm, practices]);

  const fetchPractices = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await practiceService.getPractices({ limit: 1000 });
      
      let practicesList = [];
      if (response && response.data) {
        if (response.data.success && response.data.data) {
          if (response.data.data.practices && Array.isArray(response.data.data.practices)) {
            practicesList = response.data.data.practices;
          } else if (Array.isArray(response.data.data)) {
            practicesList = response.data.data;
          }
        } else if (Array.isArray(response.data)) {
          practicesList = response.data;
        }
      }

      // Map practices with email notification status
      const practicesWithStatus = practicesList.map((practice, index) => ({
        id: practice.id || index + 1,
        practice_name: practice.practice_name || 'Unnamed Practice',
        evitals_monitoring: practice.evitals_monitoring ?? practice.evitalsMonitoring ?? 'Yes',
        // Check if any email notification is enabled (for "Add Patient" notifications)
        // Based on the practice form, these are the fields for email notifications
        email_notifications_enabled: Boolean(
          practice.email_notifications_for_provider ||
          practice.email_notifications_for_superadmin ||
          practice.email_notifications_for_practice_caregiver ||
          (isEvitalsMonitoringPractice(practice) && practice.email_notifications_for_system_caregiver)
        ),
        // Store original values for update
        originalData: {
          email_notifications_for_provider: Boolean(practice.email_notifications_for_provider),
          email_notifications_for_superadmin: Boolean(practice.email_notifications_for_superadmin),
          email_notifications_for_practice_caregiver: Boolean(practice.email_notifications_for_practice_caregiver),
          email_notifications_for_system_caregiver: Boolean(practice.email_notifications_for_system_caregiver)
        }
      }));

      setPractices(practicesWithStatus);
      setFilteredPractices(practicesWithStatus);
    } catch (err) {
      console.error('Error fetching practices:', err);
      setError('Failed to load practices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleNotification = async (practiceId) => {
    const practice = practices.find(p => p.id === practiceId);
    if (!practice) return;

    setUpdatingIds(prev => new Set(prev).add(practiceId));
    setUpdateError('');

    try {
      const newStatus = !practice.email_notifications_enabled;
      const allowSystemCaregiver = isEvitalsMonitoringPractice(practice);
      
      // Prepare update data - toggle all email notification fields
      const updateData = {
        email_notifications_for_provider: newStatus,
        email_notifications_for_superadmin: newStatus,
        email_notifications_for_practice_caregiver: newStatus,
        email_notifications_for_system_caregiver: allowSystemCaregiver ? newStatus : false
      };

      const response = await practiceService.updatePractice(practiceId, updateData);

      if (response.data && (response.data.success || response.data)) {
        // Update local state
        setPractices(prevPractices =>
          prevPractices.map(p =>
            p.id === practiceId
              ? {
                  ...p,
                  email_notifications_enabled: newStatus,
                  originalData: {
                    email_notifications_for_provider: newStatus,
                    email_notifications_for_superadmin: newStatus,
                    email_notifications_for_practice_caregiver: newStatus,
                    email_notifications_for_system_caregiver: allowSystemCaregiver ? newStatus : false
                  }
                }
              : p
          )
        );
      } else {
        setUpdateError('Failed to update notification settings. Please try again.');
      }
    } catch (err) {
      console.error('Error updating practice:', err);
      setUpdateError(err.response?.data?.message || 'Failed to update notification settings. Please try again.');
    } finally {
      setUpdatingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(practiceId);
        return newSet;
      });
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredPractices.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedPractices = filteredPractices.slice(startIndex, endIndex);

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleFirstPage = () => {
    setCurrentPage(1);
  };

  const handleLastPage = () => {
    if (totalPages > 0) {
      setCurrentPage(totalPages);
    }
  };

  return (
  <div
    className="header-screen-overlay"
    role="dialog"
    aria-modal="true"
  >
    <div className="header-screen-card email-notification-card">
      <div className="header-screen-card-header">
        <div>
          <h2>Email Notifications Settings</h2>
          <p className="header-screen-subtitle">
            Control practice level notifications for patient addition events.
          </p>
        </div>
        <button
          type="button"
          className="header-screen-close"
          aria-label="Close email notifications panel"
          onClick={onClose}
        >
          &times;
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" style={{ margin: '20px', padding: '12px', borderRadius: '4px', backgroundColor: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb' }}>
          {error}
          <button
            onClick={fetchPractices}
            style={{ marginLeft: '10px', padding: '4px 8px', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      )}

      {updateError && (
        <div className="alert alert-danger" style={{ margin: '20px', padding: '12px', borderRadius: '4px', backgroundColor: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb' }}>
          {updateError}
        </div>
      )}

      <div className="email-notification-toolbar">
        <SearchBar
          label="Search:"
          value={searchTerm}
          onChange={(val) => setSearchTerm(val)}
          placeholder="Search practices"
          onClear={() => setSearchTerm('')}
        />
      </div>

      <div className="email-notification-table-wrapper">
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <i className="fa fa-spinner fa-spin" style={{ fontSize: '24px', marginRight: '10px' }}></i>
            Loading practices...
          </div>
        ) : filteredPractices.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            {searchTerm ? 'No practices found matching your search.' : 'No practices found.'}
          </div>
        ) : (
        <table className="email-notification-table">
          <thead>
            <tr>
              <th style={{ width: '80px' }}>#</th>
              <th>Practice Name</th>
              <th style={{ width: '310px', whiteSpace: 'nowrap' }}>Email Notifications Add Patient</th>
            </tr>
          </thead>
          <tbody>
              {paginatedPractices.map((practice, index) => (
                <tr key={practice.id}>
                  <td>{startIndex + index + 1}</td>
                  <td>{practice.practice_name}</td>
                <td>
                    <button
                      type="button"
                      className={`status-pill status-${practice.email_notifications_enabled ? 'enabled' : 'disabled'}`}
                      onClick={() => handleToggleNotification(practice.id)}
                      disabled={updatingIds.has(practice.id)}
                      style={{
                        cursor: updatingIds.has(practice.id) ? 'not-allowed' : 'pointer',
                        opacity: updatingIds.has(practice.id) ? 0.6 : 1,
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      {updatingIds.has(practice.id) ? (
                        <>
                          <i className="fa fa-spinner fa-spin" style={{ marginRight: '4px' }}></i>
                          Updating...
                        </>
                      ) : (
                        practice.email_notifications_enabled ? 'Enabled' : 'Disabled'
                      )}
                    </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>

      <ListScreenPaginationBar
        entriesValue={pageSize}
        onEntriesChange={(val) => {
          setPageSize(val === 'all' ? 9999 : Number(val));
          setCurrentPage(1);
        }}
        entryOptions={[
          { value: '10', label: '10' },
          { value: '30', label: '30' },
          { value: '50', label: '50' },
          { value: '100', label: '100' },
          { value: 'all', label: 'All' }
        ]}
        currentPage={currentPage}
        totalRecords={filteredPractices.length}
        totalPages={totalPages}
        onPageChange={(page) => setCurrentPage(page)}
      />
    </div>
  </div>
);
};

export default EmailNotificationsScreen;